import { useRef, useState, type DragEvent } from "react";

interface FolderPickerProps {
  onChoose: (files: FileList | File[]) => void;
  variant?: "project" | "skill";
  selection?: {
    title: string;
    description: string;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

interface DirectoryHandle {
  kind: "directory";
  name: string;
  values(): AsyncIterableIterator<DirectoryHandle | FileHandle>;
}

interface FileHandle {
  kind: "file";
  name: string;
  getFile(): Promise<File>;
}

type DirectoryPickerWindow = Window & {
  showDirectoryPicker?: (options?: {
    id?: string;
    mode?: "read";
    startIn?: "documents";
  }) => Promise<DirectoryHandle>;
};

export function FolderPicker({
  onChoose,
  variant = "project",
  selection,
  secondaryAction,
}: FolderPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [readingDrop, setReadingDrop] = useState(false);
  const [dropError, setDropError] = useState("");
  const directoryProps = { webkitdirectory: "", directory: "" } as Record<string, string>;
  const disabled = readingDrop;
  const isSkill = variant === "skill";

  function startDrag(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (disabled) return;
    dragDepth.current += 1;
    setDragging(true);
  }

  function continueDrag(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (disabled) return;
    event.dataTransfer.dropEffect = "copy";
  }

  function endDrag(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  }

  async function acceptDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (disabled) return;

    setReadingDrop(true);
    setDropError("");
    try {
      const files = await filesFromDrop(event.dataTransfer);
      if (files.length === 0) {
        setDropError("We could not find any files in that drop. Try choosing the project folder instead.");
        return;
      }
      onChoose(files);
    } catch {
      setDropError("We could not read that drop. Try choosing the project folder instead.");
    } finally {
      setReadingDrop(false);
    }
  }

  async function chooseFolder() {
    const picker = isSkill ? (window as DirectoryPickerWindow).showDirectoryPicker : undefined;
    if (!picker) {
      inputRef.current?.click();
      return;
    }
    setReadingDrop(true);
    setDropError("");
    try {
      const directory = await picker({
        id: "doorway-claude-skills",
        mode: "read",
        startIn: "documents",
      });
      const files = await filesFromDirectoryHandle(directory);
      if (files.length === 0) {
        setDropError("We could not find any files in that folder. Choose the folder that contains SKILL.md.");
        return;
      }
      onChoose(files);
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      setDropError("We could not read that folder. Try dragging the skill here instead.");
    } finally {
      setReadingDrop(false);
    }
  }

  return (
    <div
      className={`folder-picker oasis-folder-picker${isSkill ? " oasis-folder-picker-skill" : ""}${dragging ? " folder-picker-dragging" : ""}`}
      data-testid={isSkill ? "skill-dropzone" : "project-dropzone"}
      onDragEnter={startDrag}
      onDragOver={continueDrag}
      onDragLeave={endDrag}
      onDrop={(event) => void acceptDrop(event)}
    >
      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        multiple
        {...directoryProps}
        onChange={(event) => {
          if (event.currentTarget.files?.length) onChoose(event.currentTarget.files);
        }}
      />
      {isSkill ? (
        <>
          <DoorwaySkillMark />
          <div className="folder-picker-copy">
            <strong>
              {dragging
                ? "Let go to use this skill"
                : selection?.title ?? "Drop a skill folder here"}
            </strong>
            <span>
              {selection?.description
                ?? "Choose the folder that contains SKILL.md."}
            </span>
          </div>
          <button className="secondary-button" disabled={disabled} onClick={() => void chooseFolder()} type="button">
            {disabled ? "Reading your skill…" : selection ? "Choose a different folder" : "Browse skills folder"}
          </button>
          <p>First time on Mac: press ⌘⇧G, open ~/.claude/skills, then choose the skill folder. Your browser remembers the location.</p>
        </>
      ) : (
        <>
          <span className="free-plan-note"><span aria-hidden="true" />One app free, no card required</span>
          <div className="dw-badge" role="img" aria-label="Drop your app folder">
            <div className="dw-shadow" />
            <div className="dw-arch">
              <div className="dw-doc">
                <div className="dw-doc-body" />
                <div className="dw-doc-tab" />
              </div>
              <div className="dw-arrow">
                <svg className="dw-arrow-glyph" viewBox="0 0 32 34" aria-hidden="true" focusable="false">
                  <path d="M16 2V23M5 13L16 24L27 13" />
                </svg>
              </div>
            </div>
            <div className="dw-dot" />
          </div>
          <div className="folder-picker-copy">
            <strong>{dragging ? "Let go to check your app" : "Drop your app folder here"}</strong>
            <span>or drag in the finished files</span>
          </div>
          <button className="primary-button" disabled={disabled} onClick={() => inputRef.current?.click()} type="button">
            {disabled ? "Reading your files…" : "Choose project folder"}
          </button>
          {secondaryAction && (
            <button className="folder-picker-secondary-action" disabled={disabled} onClick={secondaryAction.onClick} type="button">
              {secondaryAction.label}
            </button>
          )}
          <p>Doorway checks everything on your computer first. Nothing uploads until you choose Publish.</p>
        </>
      )}
      {dropError && <p className="drop-error" role="alert">{dropError}</p>}
    </div>
  );
}

function DoorwaySkillMark() {
  return (
    <span className="folder-picker-skill-mark" aria-hidden="true">
      <span />
    </span>
  );
}

async function filesFromDrop(dataTransfer: DataTransfer): Promise<File[]> {
  const directFiles = Array.from(dataTransfer.files ?? []);
  const fileItems = Array.from(dataTransfer.items ?? []).filter((item) => item.kind === "file");
  const entries: FileSystemEntry[] = [];
  let incompleteTraversal = false;

  for (const item of fileItems) {
    const getEntry = (item as { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry;
    const entry = typeof getEntry === "function" ? getEntry.call(item) : null;
    if (entry) entries.push(entry);
    else incompleteTraversal = true;
  }

  if (entries.length === 0) return directFiles;
  const traversedFiles = await filesFromEntries(entries);
  return incompleteTraversal ? mergeFiles(traversedFiles, directFiles) : traversedFiles;
}

async function filesFromEntries(initialEntries: FileSystemEntry[]): Promise<File[]> {
  const files: File[] = [];
  const queue = [...initialEntries];
  const syntheticRoot = initialEntries.length > 1 ? "doorway-drop" : "";
  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const entry = queue[cursor];
    if (!entry) continue;
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => {
        (entry as FileSystemFileEntry).file(resolve, reject);
      });
      files.push(withRelativePath(file, entry.fullPath, syntheticRoot));
      continue;
    }
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    while (true) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      if (batch.length === 0) break;
      queue.push(...batch);
    }
  }
  return files;
}

async function filesFromDirectoryHandle(root: DirectoryHandle): Promise<File[]> {
  const files: File[] = [];
  const queue: Array<{ directory: DirectoryHandle; path: string }> = [{ directory: root, path: root.name }];
  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const current = queue[cursor];
    if (!current) continue;
    for await (const entry of current.directory.values()) {
      const path = `${current.path}/${entry.name}`;
      if (entry.kind === "directory") {
        queue.push({ directory: entry, path });
      } else {
        files.push(withRelativePath(await entry.getFile(), path));
      }
    }
  }
  return files;
}

function withRelativePath(file: File, fullPath: string, syntheticRoot = ""): File {
  const entryPath = fullPath.replace(/^\/+/, "");
  const relativePath = syntheticRoot && entryPath ? `${syntheticRoot}/${entryPath}` : entryPath;
  if (!relativePath) return file;
  Object.defineProperty(file, "webkitRelativePath", { configurable: true, value: relativePath });
  return file;
}

function mergeFiles(primary: File[], fallback: File[]): File[] {
  const seen = new Set(primary.map(fileFingerprint));
  return [...primary, ...fallback.filter((file) => !seen.has(fileFingerprint(file)))];
}

function fileFingerprint(file: File): string {
  return [file.name, file.size, file.type, file.lastModified].join(":");
}
